import React from 'react';
import BaseSelect, { components } from 'react-select';
import BaseCreatable from 'react-select/creatable';

// Shared styling for EVERY BlobHub Select, baked in here so every picker is
// identical app-wide (single source of truth — folds in the former
// Styles.headerSelect):
//   • Truly compact control — ~31px tall, 13px text (denser than
//     react-select's 38px default). NOTE: react-select's control height is
//     driven by its indicators, so `minHeight` alone does NOT shrink it — we
//     also trim the value container and dropdown/clear indicator padding.
//     Sizing stays flexible (no fixed heights) so multi/Creatable selects
//     still grow.
//   • No blue focus outline (react-select v5 adds one by default).
//   • menuPortal z-index raised so a portalled menu clears modals and the
//     grid-widget stacking context (only applies when menuPortalTarget is set).
//   • Selected menu row is a subtle light-blue tint with inherited (dark) text, not
//     react-select's default strong-blue-background + white-text. The default is legible for a
//     plain text label, but it clips custom/muted option content (e.g. bold/muted id lines, badges)
//     designed against a light background. Non-selected and hover (`isFocused`) rows are left as
//     react-select's defaults -- we only spread an override when `isSelected`.
// Callers should NOT re-specify control height / indicator padding. Any
// caller-provided styles layer ON TOP of this base per-part (they extend, not
// replace it), so overriding e.g. `menuPortal` z-index keeps the shared sizing.
const BASE_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 31,
    fontSize: '13px',
    boxShadow: 'none',
    borderColor: state.isFocused ? '#ced4da' : base.borderColor,
    '&:hover': { borderColor: '#ced4da' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  input: (base) => ({ ...base, margin: '0px' }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  // Selected/highlighted row tints, app-wide default. react-select's stock look is a strong blue
  // selected row (white text) with a lighter blue hover; we swap in two subtle light-blue tints with
  // normal (dark) text so custom/muted option content stays legible. The highlighted (hover/keyboard
  // `isFocused`) row gets the lighter #e6effc; the persistently-selected row gets the slightly
  // stronger #DEEBFF so the current choice reads as the more prominent of the two. isSelected wins
  // when a row is both selected and hovered.
  option: (base, state) => {
    if (state.isSelected) {
      return { ...base, backgroundColor: '#DEEBFF', color: 'inherit' };
    }
    if (state.isFocused) {
      return { ...base, backgroundColor: '#e6effc' };
    }
    return base;
  },
};

function mergeStyles(styles) {
  if (!styles) return BASE_STYLES;
  const merged = { ...styles };
  // Compose the caller's per-part fn over the shared base fn so both apply.
  for (const key of Object.keys(BASE_STYLES)) {
    const baseFn = BASE_STYLES[key];
    const callerFn = styles[key];
    merged[key] = callerFn
      ? (base, state) => callerFn(baseFn(base, state), state)
      : baseFn;
  }
  return merged;
}

// Exported (alongside `components` below) purely for direct unit testing of the shared per-part
// style fns / composition logic -- CSS-in-JS output is otherwise hard to assert in jsdom.
export { components, BASE_STYLES, mergeStyles };

export const Select = ({ styles, ...props }) => (
  <BaseSelect styles={mergeStyles(styles)} {...props} />
);

export const Creatable = ({ styles, ...props }) => (
  <BaseCreatable styles={mergeStyles(styles)} {...props} />
);

export default Select;
